import type { Module } from './types';

export const REAL_ESTATE_PLAN_CODES = ['free', 'basic', 'plus', 'pro'] as const;

export type RealEstatePlanCode = (typeof REAL_ESTATE_PLAN_CODES)[number];
export type RealEstateMarketingProductCode =
  'crm' | 'public_portal' | 'sales_proformas' | 'finance_core';

export const REAL_ESTATE_BASE_MODULE_CODE = 'real_estate' as const;
export const INVESTMENT_REFERRALS_BASE_MODULE_CODE =
  'investment_referrals' as const;
export const INVESTMENT_OPERATIONS_MODULE_CODE =
  'investment_operations' as const;
export const CRM_PIPELINE_MODULE_CODE = 'crm_pipeline' as const;
export const PUBLIC_PORTAL_MODULE_CODE = 'public_portal' as const;
export const SALES_PROFORMAS_MODULE_CODE = 'sales_proformas' as const;
export const CREDIT_COLLECTIONS_MODULE_CODE = 'credit_collections' as const;
export const FINANCIAL_REPORTING_MODULE_CODE = 'financial_reporting' as const;
export const AI_SALES_ASSISTANT_MODULE_CODE = 'ai_sales_assistant' as const;

type ModulePlanShape = Pick<
  Module,
  'code' | 'category' | 'dependencies' | 'isActive'
>;

const REAL_ESTATE_PLAN_STATIC_RUNTIME_MODULE_CODES: Record<
  RealEstatePlanCode,
  readonly string[]
> = {
  free: [CRM_PIPELINE_MODULE_CODE, PUBLIC_PORTAL_MODULE_CODE],
  basic: [
    CRM_PIPELINE_MODULE_CODE,
    PUBLIC_PORTAL_MODULE_CODE,
    SALES_PROFORMAS_MODULE_CODE,
  ],
  plus: [
    CRM_PIPELINE_MODULE_CODE,
    PUBLIC_PORTAL_MODULE_CODE,
    SALES_PROFORMAS_MODULE_CODE,
    CREDIT_COLLECTIONS_MODULE_CODE,
  ],
  pro: [
    CRM_PIPELINE_MODULE_CODE,
    PUBLIC_PORTAL_MODULE_CODE,
    SALES_PROFORMAS_MODULE_CODE,
    CREDIT_COLLECTIONS_MODULE_CODE,
    FINANCIAL_REPORTING_MODULE_CODE,
  ],
};

export const REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES: Record<
  RealEstatePlanCode,
  readonly RealEstateMarketingProductCode[]
> = {
  free: ['crm', 'public_portal'],
  basic: ['crm', 'public_portal', 'sales_proformas'],
  plus: ['crm', 'public_portal', 'sales_proformas', 'finance_core'],
  pro: ['crm', 'public_portal', 'sales_proformas', 'finance_core'],
};

export function isRealEstatePlanManagedModule(
  module: ModulePlanShape
): boolean {
  if (!module.isActive || module.category !== 'feature') {
    return false;
  }

  return (
    module.code === CRM_PIPELINE_MODULE_CODE ||
    module.code === PUBLIC_PORTAL_MODULE_CODE ||
    module.code === SALES_PROFORMAS_MODULE_CODE ||
    module.code === CREDIT_COLLECTIONS_MODULE_CODE ||
    module.code === FINANCIAL_REPORTING_MODULE_CODE
  );
}

export function listRealEstatePlanManagedModuleCodes(
  modules: readonly ModulePlanShape[]
): string[] {
  return modules
    .filter((module) => isRealEstatePlanManagedModule(module))
    .map((module) => module.code)
    .sort((left, right) => left.localeCompare(right));
}

export function resolveRealEstatePlanRuntimeModuleCodes(
  planCode: RealEstatePlanCode,
  modules: readonly ModulePlanShape[]
): string[] {
  const managedCodes = new Set(listRealEstatePlanManagedModuleCodes(modules));
  return [...REAL_ESTATE_PLAN_STATIC_RUNTIME_MODULE_CODES[planCode]]
    .filter((moduleCode) => managedCodes.has(moduleCode))
    .sort((left, right) => left.localeCompare(right));
}

export function getMatchingRealEstatePlanCodes(
  enabledModuleCodes: readonly string[],
  modules: readonly ModulePlanShape[]
): RealEstatePlanCode[] {
  const managedCodes = new Set(listRealEstatePlanManagedModuleCodes(modules));
  const currentManagedCodes = new Set(
    enabledModuleCodes.filter((moduleCode) => managedCodes.has(moduleCode))
  );

  return REAL_ESTATE_PLAN_CODES.filter((planCode) =>
    sameCodeSet(
      currentManagedCodes,
      new Set(resolveRealEstatePlanRuntimeModuleCodes(planCode, modules))
    )
  );
}

export function isFreeRealEstatePlanModuleSet(
  enabledModuleCodes: readonly string[]
): boolean {
  const enabled = new Set(enabledModuleCodes);
  return (
    enabled.has(REAL_ESTATE_BASE_MODULE_CODE) &&
    enabled.has(CRM_PIPELINE_MODULE_CODE) &&
    enabled.has(PUBLIC_PORTAL_MODULE_CODE) &&
    !enabled.has(SALES_PROFORMAS_MODULE_CODE) &&
    !enabled.has(CREDIT_COLLECTIONS_MODULE_CODE) &&
    !enabled.has(FINANCIAL_REPORTING_MODULE_CODE)
  );
}

const REAL_ESTATE_PLAN_ORDER: readonly RealEstatePlanCode[] = [
  'free',
  'basic',
  'plus',
  'pro',
];

export function resolveEnabledRealEstatePlanCode(
  enabledModuleCodes: readonly string[]
): RealEstatePlanCode | null {
  const enabled = new Set(enabledModuleCodes);

  if (
    !enabled.has(REAL_ESTATE_BASE_MODULE_CODE) ||
    !enabled.has(CRM_PIPELINE_MODULE_CODE)
  ) {
    return null;
  }

  if (enabled.has(FINANCIAL_REPORTING_MODULE_CODE)) {
    return 'pro';
  }

  if (enabled.has(CREDIT_COLLECTIONS_MODULE_CODE)) {
    return 'plus';
  }

  if (enabled.has(SALES_PROFORMAS_MODULE_CODE)) {
    return 'basic';
  }

  return 'free';
}

export function enabledModuleCodesSatisfyMinimumRealEstatePlan(
  enabledModuleCodes: readonly string[],
  minimumPlanCode: RealEstatePlanCode
): boolean {
  const currentPlanCode = resolveEnabledRealEstatePlanCode(enabledModuleCodes);

  if (!currentPlanCode) {
    return !enabledModuleCodes.includes(REAL_ESTATE_BASE_MODULE_CODE);
  }

  const currentPlanRank = REAL_ESTATE_PLAN_ORDER.indexOf(currentPlanCode);
  const minimumPlanRank = REAL_ESTATE_PLAN_ORDER.indexOf(minimumPlanCode);

  return currentPlanRank >= minimumPlanRank;
}

export function realEstatePlanSatisfiesMinimum(
  currentPlanCode: RealEstatePlanCode | null | undefined,
  minimumPlanCode: RealEstatePlanCode,
  realEstateVerticalEnabled: boolean | undefined
): boolean {
  if (!currentPlanCode) {
    return realEstateVerticalEnabled === false;
  }
  return (
    REAL_ESTATE_PLAN_ORDER.indexOf(currentPlanCode) >=
    REAL_ESTATE_PLAN_ORDER.indexOf(minimumPlanCode)
  );
}

function sameCodeSet(left: ReadonlySet<string>, right: ReadonlySet<string>) {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
}
